import { DialogProps } from '@optimus-web/core';
import { FloatingPanel } from '@mews/b2b-ui';
import { useToggle } from '@mews/framework';
import { RouteName } from '@mews-commander/enums';
import { useResolveRoute } from '@mews/routing';
import { DialogTemplateFetcher } from './TemplateFetcher/DialogTemplateFetcher';

interface DialogBackendPageProps<Data extends {} = {}> {
    routeName: RouteName;
    params?: { [key: string]: unknown };
    title: DialogProps['title'];
    opened: boolean;
    onClose: () => void;
    onSubmit?: (data: Data) => void;
}

export function DialogBackendPage<Data extends {} = {}>({
    routeName,
    params,
    title,
    opened,
    onClose,
    onSubmit,
}: DialogBackendPageProps<Data>) {
    const resolveRoute = useResolveRoute();
    const url = resolveRoute(routeName, params);

    return (
        <FloatingPanel active={opened} onClose={onClose} title={title} fullWidthTitle>
            <DialogTemplateFetcher url={url} onFetchSuccess={onSubmit} />
        </FloatingPanel>
    );
}

type UseDialogBackendPageProps<Data extends {}> = Omit<DialogBackendPageProps<Data>, 'opened' | 'onClose'>;

export function useDialogBackendPage<Data extends {}>({
    onSubmit,
    ...other
}: UseDialogBackendPageProps<Data>): [DialogBackendPageProps<Data>, () => void] {
    const [opened, toggleOpen] = useToggle();
    const handleSubmit = (data: Data) => {
        toggleOpen();
        onSubmit?.(data);
    };

    return [{ ...other, onClose: toggleOpen, onSubmit: handleSubmit, opened }, toggleOpen];
}
